import { Injectable } from '@angular/core';
import { COMMON_CONSTANTS, COMMON_CONSTANTS_Percentage, COMMON_CONSTANTS_SNAPSHOT } from '../constants/common-constants';

/**
 * Unified SignificanceService for confidence level calculations
 * 
 * Works with both:
 * - Common/Dashboard components (uses COMMON_CONSTANTS)
 * - Snapshot module components (uses COMMON_CONSTANTS_SNAPSHOT via isSnapshot flag)
 * 
 * Supports: 90%, 95%, 98%, 99% confidence levels with 95% as fallback
 */
@Injectable({
  providedIn: 'root'
})
export class SignificanceService {

  constructor() { }

  /**
   * Get the lower significance limit based on confidence level
   * @param confidenceLevel The confidence level (e.g., '90%', '95%', '98%', '99%')
   * @param isSnapshot Optional - if true, uses COMMON_CONSTANTS_SNAPSHOT instead of COMMON_CONSTANTS
   * @returns The lower significance limit value
   */
  getSigLowerLimit(confidenceLevel: string, isSnapshot: boolean = false): number {
    const constants = isSnapshot ? COMMON_CONSTANTS_SNAPSHOT : COMMON_CONSTANTS;
    
    // Support both string format ('90%') and COMMON_CONSTANTS_Percentage enum values
    const level = confidenceLevel?.trim() || '95%';
    
    switch (level) {
      case '90%':
      case COMMON_CONSTANTS_Percentage?.ConfidencePercent_90:
        return constants.SigLowerLimit_90;
      case '95%':
      case COMMON_CONSTANTS_Percentage?.ConfidencePercent_95:
        return constants.SigLowerLimit_95;
      case '98%':
      case COMMON_CONSTANTS_Percentage?.ConfidencePercent_98:
        return constants.SigLowerLimit_98;
      case '99%':
      case COMMON_CONSTANTS_Percentage?.ConfidencePercent_99:
        return constants.SigLowerLimit_99;
      default:
        return constants.SigLowerLimit_95;
    }
  }

  /**
   * Get the upper significance limit based on confidence level
   * @param confidenceLevel The confidence level (e.g., '90%', '95%', '98%', '99%')
   * @param isSnapshot Optional - if true, uses COMMON_CONSTANTS_SNAPSHOT instead of COMMON_CONSTANTS
   * @returns The upper significance limit value
   */
  getSigUpperLimit(confidenceLevel: string, isSnapshot: boolean = false): number {
    const constants = isSnapshot ? COMMON_CONSTANTS_SNAPSHOT : COMMON_CONSTANTS;
    
    // Support both string format ('90%') and COMMON_CONSTANTS_Percentage enum values
    const level = confidenceLevel?.trim() || '95%';
    
    switch (level) {
      case '90%':
      case COMMON_CONSTANTS_Percentage?.ConfidencePercent_90:
        return constants.SigUpperLimit_90;
      case '95%':
      case COMMON_CONSTANTS_Percentage?.ConfidencePercent_95:
        return constants.SigUpperLimit_95;
      case '98%':
      case COMMON_CONSTANTS_Percentage?.ConfidencePercent_98:
        return constants.SigUpperLimit_98;
      case '99%':
      case COMMON_CONSTANTS_Percentage?.ConfidencePercent_99:
        return constants.SigUpperLimit_99;
      default:
        return constants.SigUpperLimit_95;
    }
  }

  /**
   * Get both lower and upper limits in one call
   * @param confidenceLevel The confidence level
   * @param isSnapshot Optional - if true, uses COMMON_CONSTANTS_SNAPSHOT instead of COMMON_CONSTANTS
   * @returns Object with lower and upper limit values
   */
  getSignificanceLimits(confidenceLevel: string, isSnapshot: boolean = false): { lower: number; upper: number } {
    return {
      lower: this.getSigLowerLimit(confidenceLevel, isSnapshot),
      upper: this.getSigUpperLimit(confidenceLevel, isSnapshot)
    };
  }
}
